import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { TrainConfig, carOffset } from './train';
import { inRange } from '../track/sections';

export const G = 9.80665;

export interface SimSettings {
  gravity: number;
  friction: number; // rolling μ, steel on polyurethane ≈ 0.02–0.03
  drag: number; // k = ½ρ·Cd·A / M_ref, applied as k·v² (per unit mass)
  /** Permanent drive floor — acts like a chain lift everywhere on the circuit. */
  minSpeed: number;
}

export const DEFAULT_SIM: SimSettings = { gravity: G, friction: 0.025, drag: 0.002, minSpeed: 2.0 };

/** Seconds the train sits at the station before dispatching again. */
export const STATION_DWELL_SEC = 4;
/** Deceleration used to bleed speed into the station stop (m/s²). */
const STATION_APPROACH_DECEL = 5;
/** Minimum look-ahead distance to start station braking (m). */
const STATION_APPROACH_MIN_DIST = 45;

export type SimPhase = 'stopped' | 'dispatching' | 'running' | 'braked' | 'waiting';

/**
 * The train is a 1-D constrained rigid body: state (s, v). It is NOT a point
 * mass — gravity is summed over every car's local tangent, which is what makes
 * the train accelerate over a crest while its tail is still climbing, and what
 * makes train length a parameter that matters.
 */
export class Simulator {
  readonly runtime: TrackRuntime;
  readonly train: TrainConfig;
  readonly settings: SimSettings;

  s = 0; // arc length of the train head (front coupler)
  v = 0;
  phase: SimPhase = 'stopped';
  lapDistance = 0; // distance travelled since dispatch, for station re-entry logic
  time = 0;
  /** Remaining dwell time while `phase === 'waiting'`. */
  dwellRemaining = 0;

  private readonly fixedDt = 1 / 240;
  private accumulator = 0;
  private scratchTan = new Vector3();
  private scratchKvec = new Vector3();

  constructor(runtime: TrackRuntime, train: TrainConfig, settings: SimSettings) {
    this.runtime = runtime;
    this.train = train;
    this.settings = settings;
    this.reset();
  }

  reset(): void {
    // start with the tail of the train at the station start (or s so the whole train is on track)
    const station = this.runtime.sections.find((sec) => sec.kind === 'station');
    const trainLen = this.train.cars * this.train.carLength;
    this.s = station ? this.runtime.centerline.normalizeS(station.from + trainLen + 1) : trainLen;
    this.v = 0;
    this.phase = 'stopped';
    this.lapDistance = 0;
    this.time = 0;
    this.dwellRemaining = 0;
    this.accumulator = 0;
  }

  dispatch(): void {
    if (this.phase === 'running') return;
    this.phase = 'dispatching';
    this.lapDistance = 0;
    this.dwellRemaining = 0;
  }

  /** Advance by a render-frame delta; substeps at a fixed 1/240 s inside. */
  update(dt: number, speedFactor = 1): void {
    if (this.phase === 'stopped' || this.phase === 'braked') return;
    const scaled = Math.min(dt, 0.25) * speedFactor;
    if (this.phase === 'waiting') {
      this.dwellRemaining -= scaled;
      this.time += scaled;
      if (this.dwellRemaining <= 0) {
        this.dwellRemaining = 0;
        this.dispatch();
      }
      return;
    }
    this.accumulator += scaled;
    while (this.accumulator >= this.fixedDt) {
      this.step(this.fixedDt);
      this.accumulator -= this.fixedDt;
    }
  }

  /** One fixed step: RK4 over (s, v), then min-speed / brake / station clamps. */
  step(dt: number): void {
    if (this.phase === 'waiting') {
      this.dwellRemaining -= dt;
      this.time += dt;
      if (this.dwellRemaining <= 0) {
        this.dwellRemaining = 0;
        this.dispatch();
      }
      return;
    }

    const a = (s: number, v: number) => this.accel(s, v);
    const s0 = this.s;
    const v0 = this.v;

    const k1s = v0;
    const k1v = a(s0, v0);
    const k2s = v0 + (dt / 2) * k1v;
    const k2v = a(s0 + (dt / 2) * k1s, v0 + (dt / 2) * k1v);
    const k3s = v0 + (dt / 2) * k2v;
    const k3v = a(s0 + (dt / 2) * k2s, v0 + (dt / 2) * k2v);
    const k4s = v0 + dt * k3v;
    const k4v = a(s0 + dt * k3s, v0 + dt * k3v);

    const ds = (dt / 6) * (k1s + 2 * k2s + 2 * k3s + k4s);
    this.s = this.runtime.centerline.normalizeS(this.s + ds);
    this.v = v0 + (dt / 6) * (k1v + 2 * k2v + 2 * k3v + k4v);
    this.lapDistance += Math.abs(ds);
    this.time += dt;

    const approachingStation = this.isApproachingStation();

    // Permanent drive: floor at minSpeed everywhere (like a chain lift on the whole circuit).
    // Brakes below may still pull below this; station approach/stop overrides it.
    if (
      (this.phase === 'running' || this.phase === 'dispatching') &&
      this.v < this.settings.minSpeed &&
      !approachingStation
    ) {
      this.v = this.settings.minSpeed;
    }

    // Dispatch: leave the station once the whole train is clear.
    if (this.phase === 'dispatching') {
      if (!this.inSection('station')) this.phase = 'running';
    }

    // Brake sections: decelerate toward target, never below.
    const brake = this.brakeAt();
    if (brake && this.v > brake.target) {
      this.v = Math.max(brake.target, this.v - brake.decel * dt);
    }

    // Station re-entry after a lap: brake into the stop, then dwell.
    if (approachingStation) {
      const stopS = this.stationStopS();
      const dist = this.distanceForward(this.s, stopS);
      if (dist < 0.35 || dist > this.runtime.totalLength - 5) {
        this.v = 0;
        this.phase = 'waiting';
        this.dwellRemaining = STATION_DWELL_SEC;
      } else {
        // Cap speed to the kinematic profile; crawl the rest of the way if we
        // undershoot so we still reach the stop mark.
        const profileV = Math.sqrt(2 * STATION_APPROACH_DECEL * Math.max(dist, 0));
        if (this.v > profileV) {
          this.v = Math.max(profileV, this.v - STATION_APPROACH_DECEL * dt);
        } else {
          const crawl = Math.min(1.2, profileV);
          if (this.v < crawl) this.v = crawl;
        }
      }
    }
  }

  /** Parked-head arc length: painted station stop, else circuit begin. */
  private stationStopS(): number {
    const trainLen = this.train.cars * this.train.carLength;
    const station = this.runtime.sections.find((sec) => sec.kind === 'station');
    if (station) {
      return this.runtime.centerline.normalizeS(station.from + trainLen + 1);
    }
    return this.runtime.centerline.normalizeS(trainLen + 1);
  }

  /**
   * True once the train has cleared the station after dispatch and is within
   * braking distance of the stop (or already inside a painted station).
   */
  private isApproachingStation(): boolean {
    if (this.phase !== 'running') return false;
    if (this.lapDistance <= this.train.cars * this.train.carLength + 10) return false;
    if (!this.runtime.closed && !this.runtime.sections.some((sec) => sec.kind === 'station')) {
      return false;
    }
    if (this.inSection('station')) return true;
    const dist = this.distanceForward(this.s, this.stationStopS());
    if (dist <= 0) return false;
    const stoppingDist = (this.v * this.v) / (2 * STATION_APPROACH_DECEL) + 12;
    const window = Math.max(STATION_APPROACH_MIN_DIST, stoppingDist);
    return dist < window;
  }

  /** Signed forward distance from s0 to s1 along the direction of travel. */
  private distanceForward(s0: number, s1: number): number {
    const L = this.runtime.totalLength;
    let d = s1 - s0;
    if (this.runtime.closed) {
      d = ((d % L) + L) % L;
    }
    return d;
  }

  private inSection(kind: 'station' | 'brake'): boolean {
    const L = this.runtime.totalLength;
    for (const sec of this.runtime.sections) {
      if (sec.kind !== kind) continue;
      // any car counts, not just the head
      for (let i = 0; i < this.train.cars; i++) {
        const cs = this.runtime.centerline.normalizeS(this.s + carOffset(this.train, i));
        if (inRange(cs, sec.from, sec.to, L, this.runtime.closed)) return true;
      }
    }
    return false;
  }

  private brakeAt(): { target: number; decel: number } | null {
    const L = this.runtime.totalLength;
    for (const sec of this.runtime.sections) {
      if (sec.kind !== 'brake') continue;
      const cs = this.runtime.centerline.normalizeS(this.s);
      if (inRange(cs, sec.from, sec.to, L, this.runtime.closed)) return sec;
    }
    return null;
  }

  /**
   * a = Σᵢ mᵢ(−g·t̂ᵢ.y)/M − friction − drag. Friction uses the full normal
   * force including the centripetal term, so it rises inside a loop where the
   * train is pressed into the rails.
   */
  accel(s: number, v: number): number {
    const { gravity, friction, drag } = this.settings;
    const cl = this.runtime.centerline;
    let aG = 0;
    let aFric = 0;
    const cars = this.train.cars;
    for (let i = 0; i < cars; i++) {
      const cs = cl.normalizeS(s + carOffset(this.train, i));
      cl.tanAt(cs, this.scratchTan);
      aG += -gravity * this.scratchTan.y;

      // normal force per unit mass: |v²·κ_vec − g_perp|, with g_vec = (0,−g,0)
      // and g_perp = g_vec − (g_vec·t̂)·t̂ the component the rails must react
      cl.curvatureVecAt(cs, this.scratchKvec).multiplyScalar(v * v);
      const gDotT = -gravity * this.scratchTan.y;
      this.scratchKvec.x -= -gDotT * this.scratchTan.x;
      this.scratchKvec.y -= -gravity - gDotT * this.scratchTan.y;
      this.scratchKvec.z -= -gDotT * this.scratchTan.z;
      aFric += friction * this.scratchKvec.length();
    }
    aG /= cars;
    aFric /= cars;

    const aDrag = drag * v * Math.abs(v);
    const sign = v >= 0 ? 1 : -1;
    return aG - sign * aFric - aDrag;
  }

  /**
   * With a permanent min-speed drive the train cannot stall on open track, so
   * there is no rollback point to report.
   */
  rollbackCheck(): { s: number; message: string } | null {
    return null;
  }
}

